import React from "react";
import PropTypes from "prop-types";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { styled } from "@mui/material/styles";
import SFTypography from "../typography";
import SFTooltipButtonIcon from "../tooltip/info";

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: false,
})(({ theme, bordered }) => ({
  td: {
    ...theme.typography.tableList,
    backgroundColor: theme.palette.white.main,
    ...(!bordered && { border: 0 }),
    borderBottom: `1px solid ${theme.palette.grey225.main}`,
    height: 54,
    position: "relative",
  },
  th: {
    ...theme.typography.tableHeader,
    backgroundColor: theme.palette.white.main,
    textTransform: "uppercase",
    fontSize: "11px",
    ...(!bordered && { border: 0 }),
    borderBottom: `1px solid ${theme.palette.grey225.main}`,
    height: 54,
  },
  "& .show-on-hover": {
    opacity: 0,
    transition: "opacity 0.2s ease-in-out",
  },
  "&:hover .show-on-hover": {
    opacity: 1,
  },
}));

const StyledTableContainer = styled(TableContainer, {
  shouldForwardProp: false,
})(({ theme, height, bordered }) => ({
  ...(bordered && {
    border: `1px solid ${theme.palette.grey225.main}`,
    borderRadius: 4,
  }),
  ...(height && {
    maxHeight: height,
    "& MuiTable-root": {
      height: "max-content",
    },
  }),
}));

const EmptyResultStyled = styled(SFTypography)(({ theme }) => ({
  padding: "50px 24px",
  textAlign: "center",
}));

const BasicTable = ({
  items,
  columns = [],
  hideHeader = false,
  height,
  bordered = false,
  size = "small",
  showColumnsOnHoverOnly = false,
  emptyResult = null,
}) => {
  return (
    <StyledTableContainer
      height={height}
      bordered={bordered ? bordered : undefined}
    >
      <Table size="small">
        {!hideHeader && (
          <TableHead>
            <StyledTableRow bordered={bordered ? bordered : undefined}>
              {columns.map((c) => (
                <TableCell
                  key={`narrow-table-heading-${c.label}`}
                  {...(c.align && { align: c.align })}
                  {...(c.width && { width: c.width })}
                  sx={{
                    ...(c.nowrap && { whiteSpace: "nowrap" }),
                  }}
                >
                  {c.label}
                  {c.helpText && <SFTooltipButtonIcon tooltip={c.helpText} />}
                </TableCell>
              ))}
            </StyledTableRow>
          </TableHead>
        )}
        {emptyResult && items.length == 0 && (
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ borderBottom: "none" }}>
                <EmptyResultStyled variant="body1">
                  {emptyResult}
                </EmptyResultStyled>
              </TableCell>
            </TableRow>
          </TableBody>
        )}
        {items?.length > 0 && (
          <TableBody>
            {items.map((row, index) => (
              <StyledTableRow
                bordered={bordered}
                key={`narrow-table-row-${index}`}
              >
                {columns.map((c, i) => (
                  <TableCell
                    className={
                      showColumnsOnHoverOnly !== false &&
                      showColumnsOnHoverOnly(row, c)
                        ? "show-on-hover"
                        : ""
                    }
                    size={size}
                    key={`narrow-table-row-cell-${index}-${i}`}
                    scope="row"
                    {...(c.width && { width: c.width })}
                  >
                    {c.render ? c.render(row, index) : row[c.name]}
                  </TableCell>
                ))}
              </StyledTableRow>
            ))}
          </TableBody>
        )}
      </Table>
    </StyledTableContainer>
  );
};

BasicTable.propTypes = {
  items: PropTypes.array,
  columns: PropTypes.array,
  hideHeader: PropTypes.bool,
  height: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.func,
  ]),
  bordered: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium"]),
};

export default BasicTable;
